// frontend/src/pages/MypagePoints.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { FaCoins, FaGift } from 'react-icons/fa';
import { toast } from 'react-toastify';

interface HistoryItem {
  id: number;
  amount: number;
  type: string;
  source: string;
  description: string | null;
  created_at: string;
}

interface PointData {
  balance: number;
  expires_at: string | null;
  history: HistoryItem[];
  member_type?: string | null;   // employee | family | friend | null（会社所属時のみ）
}

interface Reward {
  id: number;
  name: string;
  description: string | null;
  points_cost: number;
  reward_type: string;
  can_redeem: boolean;
}

interface MyRequest {
  id: number;
  reward_name: string;
  points_spent: number;
  status: string;
  admin_note: string | null;
  created_at: string;
}

interface RewardsData {
  balance: number;
  rewards: Reward[];
  my_requests: MyRequest[];
}

const TYPE_LABEL: Record<string, { ja: string; zh: string }> = {
  earn:   { ja: 'ポイント付与', zh: '积分获得' },
  spend:  { ja: 'ポイント利用', zh: '积分使用' },
  expire: { ja: '失効',         zh: '积分失效' },
  adjust: { ja: '管理者調整',   zh: '管理员调整' },
};

const SOURCE_DESC: Record<string, { ja: string; zh: string }> = {
  first_payment:        { ja: '初回診療完了ボーナス',   zh: '首次就诊奖励' },
  monthly_continuation: { ja: '月次継続ボーナス',        zh: '月度持续奖励' },
  quarterly_streak:     { ja: '連続3ヶ月達成ボーナス',  zh: '连续3个月达成奖励' },
  referral:             { ja: '紹介ボーナス',              zh: '介绍奖励' },
  reservation:          { ja: '診療支払い完了',          zh: '就诊支付完成' },
  redemption:           { ja: '商品交換',                zh: '积分兑换' },
  shipping:             { ja: '配送免除',                zh: '免运费' },
  system:               { ja: '有効期限切れによる失効',  zh: '积分到期失效' },
  admin:                { ja: '管理者調整',              zh: '管理员调整' },
};

const TYPE_COLOR: Record<string, string> = {
  earn:   'text-green-600 bg-green-50',
  spend:  'text-red-500 bg-red-50',
  expire: 'text-gray-400 bg-gray-50',
  adjust: 'text-blue-500 bg-blue-50',
};

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending:  { label: '審査中 / 审核中',   color: 'text-yellow-600 bg-yellow-50' },
  approved: { label: '承認済 / 已批准',   color: 'text-green-600 bg-green-50' },
  rejected: { label: '却下 / 已拒绝',     color: 'text-red-500 bg-red-50' },
};

const HistorySection: React.FC<{ data: PointData; lang: 'ja' | 'zh'; t: any }> = ({ data, lang, t }) => {
  const [showAll, setShowAll] = useState(false);
  const INITIAL = 5;
  const items = showAll ? data.history : data.history.slice(0, INITIAL);

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-700 mb-3">{t('points.history_label')}</h2>
      {data.history.length === 0 ? (
        <div className="bg-white rounded-xl p-6 text-center text-gray-400 text-sm">
          {t('points.no_history')}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="divide-y divide-gray-100">
            {items.map(h => {
              const label = TYPE_LABEL[h.type]?.[lang] || h.type;
              const color = TYPE_COLOR[h.type] || '';
              return (
                <div key={h.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${color}`}>{label}</span>
                    <div>
                      <div className="text-sm text-gray-700 leading-tight">
                        {SOURCE_DESC[h.source]?.[lang] ?? h.description ?? h.source}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{h.created_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                  <span className={`font-bold text-sm shrink-0 ml-3 ${h.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {h.amount >= 0 ? '+' : ''}{h.amount.toLocaleString()} pt
                  </span>
                </div>
              );
            })}
          </div>
          {data.history.length > INITIAL && (
            <button
              onClick={() => setShowAll(v => !v)}
              className="w-full py-2.5 text-xs text-gray-500 hover:bg-gray-50 border-t border-gray-100 transition-colors"
            >
              {showAll
                ? t('points.show_less', '折りたたむ ▲')
                : t('points.show_more', `すべて表示（${data.history.length}件） ▼`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MypagePoints: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'ja';
  const [data, setData] = useState<PointData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRedeemDlg, setShowRedeemDlg] = useState(false);
  const [rewardsData, setRewardsData] = useState<RewardsData | null>(null);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [redeeming, setRedeeming] = useState<number | null>(null);
  const [confirmReward, setConfirmReward] = useState<Reward | null>(null);

  const loadPoints = useCallback(() => {
    api.get('/points/my')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadPoints(); }, [loadPoints]);

  const openRedeemDlg = async () => {
    setShowRedeemDlg(true);
    setRewardsLoading(true);
    try {
      const res = await api.get('/points/rewards');
      setRewardsData(res.data);
    } catch {
      toast.error('交換商品の取得に失敗しました');
    } finally {
      setRewardsLoading(false);
    }
  };

  const handleRedeem = (reward: Reward) => {
    if (!reward.can_redeem) return;
    setConfirmReward(reward);  // window.confirm の代わりにカスタム確認ダイアログ
  };

  const proceedRedeem = async (reward: Reward) => {
    setConfirmReward(null);
    setRedeeming(reward.id);
    try {
      await api.post('/points/redeem', { reward_id: reward.id });  // request body 方式
      toast.success('申請が完了しました。審査をお待ちください。');
      setShowRedeemDlg(false);
      loadPoints();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || '申請に失敗しました');
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) return <div className="p-8 text-gray-400 text-center">{t('admin.common.loading')}</div>;
  if (!data) return <div className="p-8 text-gray-400 text-center">{t('admin.common.fetch_failed')}</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* 残高カード + 交換ボタン（横並び） */}
      <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl p-5 text-white mb-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5 mb-1 opacity-80 text-xs font-medium">
              <FaCoins className="text-xs" /> {t('points.balance_label', 'ポイント残高')}
            </div>
            <div className="text-4xl font-bold leading-none">
              {data.balance.toLocaleString()}
              <span className="text-lg font-normal ml-1">pt</span>
            </div>
            {data.expires_at && (() => {
              const daysLeft = Math.ceil(
                (new Date(data.expires_at).getTime() - Date.now()) / 86400000
              );
              const isUrgent  = daysLeft <= 7;
              const isWarning = daysLeft <= 30;
              return (
                <div className={[
                  'mt-2 text-xs font-medium rounded-full px-2 py-0.5 inline-block',
                  isUrgent  ? 'bg-red-500/80 text-white animate-pulse'
                  : isWarning ? 'bg-orange-400/80 text-white'
                  : 'opacity-70',
                ].join(' ')}>
                  {isUrgent && '⚠️ '}
                  {t('points.expires_at', '失効予定')}: {data.expires_at.slice(0, 10)}
                  {isWarning && ` （残り${daysLeft}日）`}
                </div>
              );
            })()}
          </div>
          <button
            onClick={openRedeemDlg}
            className="flex flex-col items-center gap-1 bg-white/20 hover:bg-white/30 border border-white/40 text-white font-semibold px-4 py-3 rounded-xl transition-colors"
          >
            <FaGift className="text-lg" />
            <span className="text-xs whitespace-nowrap">{t('points.redeem_btn', '交換する')}</span>
          </button>
        </div>
      </div>

      {/* 会社所属が確認済みのユーザーに認証済みバッジを表示（社員認証は /corp/<会社コード> のリンクのみ） */}
      {data?.member_type && (
        <div className="mb-6 bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center gap-2 shadow-sm">
          <span className="text-emerald-600 text-lg">✅</span>
          <p className="text-sm font-medium text-emerald-800">{t('points.verified_member', '認証済みです（会社への所属が確認されました）')}</p>
        </div>
      )}

      {/* ポイント獲得方法 */}
      <div className="mb-6">
        <h2 className="text-base font-semibold text-gray-700 mb-3">{t('points.how_to_earn', 'ポイント獲得方法')}</h2>
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-amber-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-amber-800 font-semibold">{t('points.earn_event', 'イベント')}</th>
                <th className="px-4 py-2 text-center text-xs text-amber-800 font-semibold">{t('points.earn_employee', '社員')}</th>
                <th className="px-4 py-2 text-center text-xs text-amber-800 font-semibold">{t('points.earn_family', '家族')}</th>
                <th className="px-4 py-2 text-center text-xs text-amber-800 font-semibold">{t('points.earn_friend', '友人')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                { event: t('points.event_first', '初回利用'),         e: 500, f: 300, fr: 100 },
                { event: t('points.event_monthly', '月次継続'),       e: 300, f: 200, fr: 100 },
                { event: t('points.event_quarterly', '3ヶ月継続達成'), e: 1000, f: 500, fr: 300 },
                { event: t('points.event_referral', '友人紹介成功'),  e: 1000, f: 500, fr: 300 },
              ].map(row => (
                <tr key={row.event} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-700">{row.event}</td>
                  <td className="px-4 py-2.5 text-center font-semibold text-amber-700">{row.e.toLocaleString()} pt</td>
                  <td className="px-4 py-2.5 text-center text-gray-600">{row.f.toLocaleString()} pt</td>
                  <td className="px-4 py-2.5 text-center text-gray-600">{row.fr.toLocaleString()} pt</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2.5 bg-gray-50 text-xs text-gray-400 space-y-0.5">
            <p>・ {t('points.note_expiry', '有効期限：最終利用日から6ヶ月')}</p>
            <p>・ {t('points.note_annual_cap', '社員の年間付与上限：7,100pt')}</p>
            <p>・ {t('points.note_monthly', '月次継続は翌月末に自動付与（前月・当月に利用がある場合）')}</p>
          </div>
        </div>
      </div>

      {/* 履歴（最初は5件、もっと見るで全件） */}
      <HistorySection data={data} lang={lang} t={t} />

      {/* 交換確認ダイアログ（window.confirm の代替） */}
      {confirmReward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-gray-800 mb-2">{t('points.confirm_title', '交換の確認')}</h3>
            <p className="text-sm text-gray-600 mb-1">「{confirmReward.name}」</p>
            <p className="text-sm text-amber-700 font-semibold mb-4">{confirmReward.points_cost.toLocaleString()} pt {t('points.will_be_spent', 'が消費されます')}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmReward(null)} className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm">{t('common.cancel', 'キャンセル')}</button>
              <button onClick={() => proceedRedeem(confirmReward)} className="flex-1 bg-amber-700 text-amber-50 rounded-lg py-2 text-sm font-medium">{t('points.confirm_redeem', '交換する')}</button>
            </div>
          </div>
        </div>
      )}

      {/* 交換ダイアログ */}
      {showRedeemDlg && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <FaGift className="text-amber-700" />
                  {t('points.redeem_title', 'ポイント交換')}
                </h2>
                <button onClick={() => setShowRedeemDlg(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {t('points.current_balance', '現在の残高')}: <span className="font-bold text-amber-700">{(rewardsData?.balance ?? data.balance).toLocaleString()} pt</span>
              </p>
            </div>

            <div className="p-5 space-y-3">
              {rewardsLoading ? (
                <p className="text-center text-gray-400 py-6">{t('admin.common.loading')}</p>
              ) : (
                <>
                  {/* 交換商品一覧 */}
                  {(rewardsData?.rewards || []).map(r => (
                    <div key={r.id} className={`border rounded-xl p-4 ${r.can_redeem ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">{r.name}</div>
                          {r.description && <div className="text-xs text-gray-500 mt-0.5">{r.description}</div>}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-amber-700 font-bold text-sm">{r.points_cost.toLocaleString()} pt</div>
                          <button
                            onClick={() => handleRedeem(r)}
                            disabled={!r.can_redeem || redeeming === r.id}
                            className={`mt-1 px-3 py-1 rounded-lg text-xs font-medium ${
                              r.can_redeem
                                ? 'bg-amber-700 text-amber-50 hover:bg-amber-800'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {redeeming === r.id ? '...' : t('points.redeem_action', '交換する')}
                          </button>

                        </div>
                      </div>
                    </div>
                  ))}

                  {/* 申請履歴 */}
                  {(rewardsData?.my_requests || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-600 mb-2">{t('points.request_history', '申請履歴')}</h3>
                      <div className="space-y-2">
                        {rewardsData!.my_requests.map(req => {
                          const s = STATUS_LABEL[req.status] || { label: req.status, color: '' };
                          return (
                            <div key={req.id} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                              <div>
                                <div className="text-gray-700">{req.reward_name}</div>
                                <div className="text-gray-400">{req.created_at?.slice(0, 10)} · {req.points_spent.toLocaleString()}pt</div>
                                {req.admin_note && <div className="text-gray-500 mt-0.5">{req.admin_note}</div>}
                              </div>
                              <span className={`px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MypagePoints;
